import { useContext, useMemo } from "react";
import { Separator } from "../ui/separator";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
} from "../ui/sidebar";
import AuthContext from "../../store/contexts/AuthContext";
import { DynamicLucideIcon } from "../puck/PageIconSelector";
import { toast } from "sonner";
import NewPageDialog from "../pages/home/NewPageDialog";
import NavigatePage from "../common/NavigatePage";
import { useTranslation } from "react-i18next";
import { Page } from "@/type/common";

const Navbar = () => {
  const { pages, categories, user, editMode, setEditMode, isEditor } =
    useContext(AuthContext);
  const { open } = useSidebar();
  const location = useLocation();
  const { t } = useTranslation();

  const handleManagePlan = () => {
    // Get the O'Dashboard portal URL from environment
    const portalUrl = import.meta.env.VITE_OSOLUTION_API || 'https://odashboard.app';
    // Get the license key from localStorage
    const licenseKey = localStorage.getItem('odash_license_key') || '';
    // Navigate to the manage plan page
    const managePlanUrl = `${portalUrl}/odash/manage-plan?key=${licenseKey}`;
    window.open(managePlanUrl, '_blank');
  };

  // Group pages by category
  const groupedPages = useMemo(() => {
    const uncategorized: Page[] = [];
    const categorized: { [key: number]: Page[] } = {};

    pages.forEach((page) => {
      if (page.category_id) {
        if (!categorized[page.category_id]) {
          categorized[page.category_id] = [];
        }
        categorized[page.category_id].push(page);
      } else {
        uncategorized.push(page);
      }
    });

    return { uncategorized, categorized };
  }, [pages]);

  const renderPageItem = (page: Page, index: number) => {
    const isFreemiumLocked = user.plan === "freemium" && index !== 0;
    const isActive = page.id === location.pathname.slice(1);

    return (
      <SidebarMenuItem key={page.id}>
        <SidebarMenuButton
          className={`${isActive ? "bg-black/5" : ""} ${
            isFreemiumLocked
              ? "opacity-50 hover:bg-transparent cursor-not-allowed"
              : ""
          }`}
          asChild
          tooltip={page.title}
        >
          {isFreemiumLocked ? (
            <div
              onClick={() =>
                toast.error(
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
                    <div>{t("editor.navbar.subscriptionRequired")}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManagePlan();
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      {t("editor.navbar.managePlan")}
                    </button>
                  </div>,
                  {
                    style: {
                      maxWidth: '500px',
                    },
                  }
                )
              }
            >
              <DynamicLucideIcon name={page.icon} size={18} />
              <span>{page.title}</span>
            </div>
          ) : (
            <Link to={`/${page.id}`}>
              <DynamicLucideIcon name={page.icon} size={18} />
              <span>{page.title}</span>
            </Link>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <NavigatePage />
      {open && (
        <SidebarHeader className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{t("editor.navbar.pages")}</h2>
            {isEditor && <NewPageDialog />}
          </div>
        </SidebarHeader>
      )}

      <SidebarContent className={open ? "p-4" : "p-2"}>
        {open && <Separator className="mb-4" />}
        {pages.length === 0 && open ? (
          <p className="text-sm text-gray-500">
            {t("editor.navbar.noPages")}
          </p>
        ) : (
          <div className="space-y-4">
            {/* Categorized pages */}
            {categories
              .filter((category) => groupedPages.categorized[category.id]?.length > 0)
              .map((category) => (
                <SidebarGroup key={category.id}>
                  {open && (
                    <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase">
                      {category.name}
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {groupedPages.categorized[category.id].map((page, index) =>
                        renderPageItem(page, pages.indexOf(page))
                      )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}

            {/* Uncategorized pages */}
            {groupedPages.uncategorized.length > 0 && (
              <SidebarGroup>
                {open && categories.some((cat) => groupedPages.categorized[cat.id]?.length > 0) && (
                  <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase">
                    {t("editor.navbar.otherPages")}
                  </SidebarGroupLabel>
                )}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {groupedPages.uncategorized.map((page, index) =>
                      renderPageItem(page, pages.indexOf(page))
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </div>
        )}
      </SidebarContent>
      {isEditor && (
        <SidebarFooter>
          <div>
            <span className="text-xs text-muted-foreground">
              {t("AppVersion")}: {import.meta.env.VITE_APP_VERSION}
            </span>
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};

export default Navbar;
